import { useMemo, useState } from 'react';
import ArticleCard from '@/components/base/ArticleCard';
import type { Article } from '@/types';

const sortOptions = [
  { key: 'latest', label: '最新发布' },
  { key: 'hot', label: '阅读最多' },
  { key: 'discussed', label: '讨论最多' },
] as const;

type SortKey = (typeof sortOptions)[number]['key'];

const PAGE_SIZE = 6;

export default function CategoryContent({ articles }: { articles: Article[] }) {
  const [sort, setSort] = useState<SortKey>('latest');
  const [page, setPage] = useState(1);

  const sorted = useMemo(() => {
    const list = [...articles];
    if (sort === 'hot') list.sort((a, b) => b.views - a.views);
    if (sort === 'discussed') list.sort((a, b) => b.comments - a.comments);
    return list;
  }, [articles, sort]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const visible = sorted.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const changeSort = (key: SortKey) => {
    setSort(key);
    setPage(1);
  };

  return (
    <div>
      <div className="mb-6 flex flex-col gap-3 rounded-xl border border-background-200 bg-background-100 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-2" role="tablist" aria-label="排序方式">
          {sortOptions.map((option) => (
            <button
              key={option.key}
              type="button"
              role="tab"
              aria-selected={sort === option.key}
              onClick={() => changeSort(option.key)}
              className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors ${
                sort === option.key
                  ? 'bg-foreground-950 text-background-50'
                  : 'text-foreground-700 hover:bg-background-200'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
        <span className="text-xs text-foreground-500">
          共 {sorted.length} 篇 · 第 {currentPage}/{totalPages} 页
        </span>
      </div>

      {visible.length > 0 ? (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          {visible.map((article) => (
            <ArticleCard key={article.id} article={article} />
          ))}
        </div>
      ) : (
        <div className="rounded-xl border border-background-200 bg-background-50 px-6 py-16 text-center text-sm text-foreground-500">
          该栏目还在等第一条爆料，稍后再来看看
        </div>
      )}

      {totalPages > 1 && (
        <nav className="mt-8 flex items-center justify-center gap-2" aria-label="分页">
          <button
            type="button"
            disabled={currentPage === 1}
            onClick={() => setPage((value) => Math.max(1, value - 1))}
            className="flex h-9 items-center gap-1 rounded-md border border-background-300 px-3 text-sm text-foreground-700 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <i className="ri-arrow-left-s-line" />
            上一页
          </button>
          {Array.from({ length: totalPages }).map((_, index) => (
            <button
              key={index}
              type="button"
              onClick={() => setPage(index + 1)}
              className={`h-9 w-9 rounded-md text-sm transition-colors ${
                currentPage === index + 1
                  ? 'bg-primary-500 text-background-50'
                  : 'border border-background-300 text-foreground-700 hover:bg-background-100'
              }`}
            >
              {index + 1}
            </button>
          ))}
          <button
            type="button"
            disabled={currentPage === totalPages}
            onClick={() => setPage((value) => Math.min(totalPages, value + 1))}
            className="flex h-9 items-center gap-1 rounded-md border border-background-300 px-3 text-sm text-foreground-700 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-40"
          >
            下一页
            <i className="ri-arrow-right-s-line" />
          </button>
        </nav>
      )}
    </div>
  );
}